import { useMemo } from 'react';

import path from 'path';

import { useTranslation } from 'react-i18next';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';

import ErrorPage from '~components/errorBoundary/errorPage';
import ErrorType from '~components/errorBoundary/errorType';
import { pagePathSegments, queryParameters } from '~constants';
import { UserRoleEnum } from '~enums';
import KeyTypeEnum from '~enums/keyTypeEnum';
import useAuthorize from '~hooks/useAuthorize';
import PageLayout from '~layouts/page/pageLayout';
import { getService } from '~services/serviceFactory';

import KeyDetailsContainer from '../containers/keyDetailsContainer';
import KeyOfflineAccessContainer from '../containers/keyOfflineAccessContainer';
import KeyContext from '../providers/keyContext';
import KeysService from '../services/keysService';

interface Props {
	tab: 'general' | 'offline-access';
}
const keysService = getService(KeysService);

const KeyDetailsPage = ({ tab }: Props) => {
	const { t } = useTranslation('entities');
	const auth = useAuthorize();
	const { id: keyId } = useParams();
	const [searchParams] = useSearchParams();
	const organisationId = searchParams.get(queryParameters.OverwrittenClientId) ?? undefined;
	const isSuperAdmin = auth.authorize([UserRoleEnum.SuperAdmin]);

	// Load key
	const {
		data: key,
		error: keyError,
		isLoading,
	} = useSWR(keysService.getKeyByIdCacheKey(keyId, organisationId), ([_, id, orgId]) =>
		id ? keysService.getKeyById(id, orgId ?? undefined) : undefined
	);

	const isSecurePhysicalKey = useMemo(() => {
		return key?.type === KeyTypeEnum.SecurePhysicalKey;
	}, [key?.type]);

	const showOfflineAccessTab = isSecurePhysicalKey && isSuperAdmin;

	const tabs = useMemo(
		() =>
			keyId ?
				[
					{
						key: pagePathSegments.General,
						title: t('key.fields.generalInformation', { ns: 'entities' }),
						path: path.join('/', pagePathSegments.Keys, keyId, pagePathSegments.General),
						component: <KeyDetailsContainer />,
					},
					...(showOfflineAccessTab ?
						[
							{
								key: pagePathSegments.OfflineAccess,
								title: t('offlineAccess', { ns: 'nav' }),
								path: path.join('/', pagePathSegments.Keys, keyId, pagePathSegments.OfflineAccess),
								component: <KeyOfflineAccessContainer />,
							},
						]
					:	[]),
				]
			:	[],
		[keyId, organisationId, showOfflineAccessTab, t]
	);
	const crumbs = useMemo(
		() => [
			{
				title: t('key.labels.plural', { ns: 'entities' }),
				path: path.join('/', pagePathSegments.Keys),
			},
			{
				title: key?.name ?? keyId ?? '',
				path: '',
			},
		],
		[key]
	);

	const computedTitle = useMemo(
		() => key?.name ?? `${t('key.labels.single', { ns: 'entities' })} ${keyId ?? ''}`,
		[key]
	);

	// --- All hooks above, all early returns below ---

	if (keyId == undefined) {
		return <Navigate to={pagePathSegments.Keys} replace />;
	}

	if (!isLoading && keyError) {
		return <ErrorPage type={ErrorType.NotFound} />;
	}

	return (
		<KeyContext
			value={{
				key: key,
				overwrittenClientId: organisationId,
			}}
		>
			<PageLayout
				isLoading={isLoading}
				pageTitle={computedTitle}
				preservedTabSearchParams={[queryParameters.OverwrittenClientId]}
				headerInfo={{
					title: computedTitle,
				}}
				activeTabKey={tab}
				tabs={tabs}
				crumbs={crumbs}
			/>
		</KeyContext>
	);
};

export default KeyDetailsPage;
